import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 建立 axios 實例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 請求攔截器：自動附加 JWT Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function clearTokenAndGoLogin() {
  localStorage.removeItem('token');
  window.location.href = '/login';
}

/** Flask-JWT-Extended 對部分解碼錯誤曾回 422；與舊後端相容 */
function isJwtAuthFailure422(status, data) {
  if (status !== 422) return false;
  const msg = typeof data?.msg === 'string' ? data.msg : '';
  const lower = msg.toLowerCase();
  return (
    lower.includes('signature verification') ||
    lower.includes('not enough segments') ||
    lower.includes('token has expired') ||
    lower.includes('invalid token') ||
    lower.includes('expired signature')
  );
}

// 回應攔截器：處理錯誤
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const reqUrl = error.config?.url || '';
      const isLoginAttempt = reqUrl.includes('/auth/login');
      if (!isLoginAttempt && (status === 401 || isJwtAuthFailure422(status, data))) {
        clearTokenAndGoLogin();
      }
    }
    return Promise.reject(error);
  }
);

// 認證 API
export const login = (username, password) => {
  return api.post('/auth/login', { username, password });
};

export const getCurrentUser = () => {
  return api.get('/auth/me');
};

export const changePassword = (currentPassword, newPassword) => {
  return api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

export const checkInitStatus = () => {
  return api.get('/auth/init-status').then(res => res.data);
};

// 聊天 API
export const sendMessage = (message, conversationId) => {
  return api.post('/chat/message', {
    message,
    conversation_id: conversationId,
  });
};

// 用戶管理 API（管理員專用）
export const getUsers = (page = 1, perPage = 20, includeDeleted = false) => {
  return api.get('/users', {
    params: { page, per_page: perPage, include_deleted: includeDeleted },
  });
};

export const getUser = (userId) => {
  return api.get(`/users/${userId}`);
};

export const createUser = (userData) => {
  return api.post('/users', userData);
};

export const updateUser = (userId, userData) => {
  return api.put(`/users/${userId}`, userData);
};

export const updateUserPermissions = (userId, permissions) => {
  return api.put(`/users/${userId}/permissions`, permissions);
};

export const resetUserPassword = (userId, newPassword) => {
  return api.post(`/users/${userId}/reset-password`, {
    new_password: newPassword,
  });
};

export const deleteUser = (userId) => {
  return api.delete(`/users/${userId}`);
};

export const restoreUser = (userId) => {
  return api.post(`/users/${userId}/restore`);
};

// 專案管理 API
export const getProjects = () => {
  return api.get('/projects');
};

export const getProject = (projectId) => {
  return api.get(`/projects/${projectId}`);
};

// 應用設定（管理員）：Dify
export const getDifySettings = () => {
  return api.get('/app/settings/dify');
};

export const updateDifySettings = (payload) => {
  return api.put('/app/settings/dify', payload);
};

export const getRedmineSettings = () => {
  return api.get('/app/settings/redmine');
};

export const updateRedmineSettings = (payload) => {
  return api.put('/app/settings/redmine', payload);
};

export const testRedmineConnection = () => {
  return api.post('/app/settings/redmine/test-connection');
};

// Redmine 同步
export const triggerRedmineSync = (dryRun = false) => {
  return api.post('/app/settings/redmine/sync', { dry_run: dryRun });
};

export const getRedmineSyncJob = (jobId) => {
  return api.get(`/app/settings/redmine/sync/jobs/${jobId}`);
};

export const getRedmineSyncJobLatest = () => {
  return api.get('/app/settings/redmine/sync/jobs/latest');
};

export default api;
